import React, { useCallback, useEffect, useState } from 'react';
import { ChevronRight, ExternalLink, Loader2, Check, X, RotateCcw, Wrench, Lightbulb, Activity, GitBranch, History, Inbox } from 'lucide-react';
import { useBreadcrumb, useHideGlobalTopBar } from 'shared/context/BreadcrumbContext';
import { useAppDialog } from 'shared/context/AppDialogContext';
import {
  listProposals, getProposal, approveProposal, rejectProposal, revertProposal, confirmBackfill,
} from 'features/evals3/api/evals3Api';
import { blastRadiusSentence, regressionSentence } from 'features/evals3/blastRadiusText';
import ProposalGraph from 'features/evals3/editor/ProposalGraph';
import AgentRunDetailPage from 'features/agent-analytics/pages/AgentRunDetailPage';
import TraceDetailPage from 'features/agent-analytics/pages/TraceDetailPage';
import ConversationDetailDrawer from 'features/agent-analytics/components/ConversationDetailDrawer';
import BrandLoader, { PageLoader } from 'shared/components/ui/BrandLoader';

/**
 * Proposed changes to the new evals (TRO-435): a person approves
 * consequences, not code.
 *
 * Each proposal says, in this order: what it is for, why it came up, whether
 * it would get anything people labelled wrong, how many recent verdicts it
 * would change (with real traces each way), and which step it touches on the
 * graph. The actual rule, anchor or prompt is behind an expander for
 * engineers; approving never requires opening it.
 *
 * The change is already written as a version of the eval, unlabelled.
 * Approve → the production label moves to it, then the eval's backfill policy
 * (a costly backfill asks first). Reject → needs a reason, because a
 * rejection teaches the next proposal. Revert → the label goes back to the
 * version the change was based on; the history is never rewritten (TRO-461).
 */

export const KIND_TITLE = {
  accuracy: 'Make the eval more accurate',
  cost_pre_screen: 'Catch a known failure cheaply',
  cost_retire: 'Remove a step that never changes anything',
  cost_pass_screen: 'Let traces that always pass skip the judge',
};
const ARTIFACT = {
  rule: 'a rule change', anchor: 'worked examples for the judge', prompt: 'a new judge prompt',
  pre_screen: 'a new pre-screen step', retire: 'removing a step', pass_screen: 'a new pass-screen step',
};
const MODE_WORD = { misroute: 'sent traces down the wrong branch', bad_short_circuit: 'stopped traces it should have let through', misjudgment: 'scored traces wrongly' };

const verdictWord = (passed) => (passed ? 'PASS' : 'FAIL');

function ago(iso) {
  if (!iso) return '';
  const sec = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60); if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60); if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

/** A section of the proposal: a soft card with a small icon and a label, not a ruled box. */
function Block({ label, icon: Icon = null, children, testId, tone = 'plain' }) {
  const tint = tone === 'brand'
    ? 'bg-brand-soft border-transparent'
    : 'bg-surface border-[color:var(--border-subtle)]';
  return (
    <section data-testid={testId} className={`rounded-xl border ${tint} p-4 shadow-token-sm`}>
      <div className="mb-2 flex items-center gap-1.5 text-[11px] font-medium text-[color:var(--text-muted)]">
        {Icon && <Icon className="h-3.5 w-3.5" aria-hidden />}
        {label}
      </div>
      {children}
    </section>
  );
}

/** A small rounded status word, the way the feed and the header both say where a change stands. */
function StatusPill({ p }) {
  const map = {
    open: ['Waiting for you', 'bg-brand-soft text-brand'],
    needs_human: ['Needs a person', 'bg-warning-soft text-warning'],
    approved: ['In production', 'bg-success-soft text-success'],
    auto_promoted: ['Applied automatically', 'bg-success-soft text-success'],
    rejected: ['Rejected', 'bg-[color:var(--surface-sunken)] text-[color:var(--text-muted)]'],
    superseded: ['Closed', 'bg-[color:var(--surface-sunken)] text-[color:var(--text-muted)]'],
  };
  const [word, cls] = p.reverted_at ? ['Reverted', 'bg-[color:var(--surface-sunken)] text-[color:var(--text-muted)]'] : (map[p.status] || map.superseded);
  return <span className={`inline-flex h-5 shrink-0 items-center rounded-full px-2 text-[10.5px] font-medium ${cls}`}>{word}</span>;
}

/** Today / Yesterday / This week / Earlier — a feed reads by when. */
function dayGroup(iso) {
  const d = new Date(iso); const now = new Date();
  const start = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((start(now) - start(d)) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return 'This week';
  return 'Earlier';
}

function unitOf(row) {
  if (row.target_kind === 'span') return { kind: 'span', id: row.span_id };
  if (row.target_kind === 'conversation') return { kind: 'conversation', id: row.conversation_id };
  return { kind: 'run', id: row.run_id };
}

function Verdict({ passed }) {
  return <span className={`rounded px-1.5 py-px text-[10.5px] font-semibold ${passed ? 'bg-success-soft text-success' : 'bg-danger-soft text-danger'}`}>{verdictWord(passed)}</span>;
}

function FlipList({ rows, onOpen }) {
  return (
    <ul className="flex flex-col gap-1">
      {rows.map((r) => (
        <li key={r.eval_run_id} className="group flex items-center gap-2 rounded-lg bg-[color:var(--surface-sunken)] px-2.5 py-1.5 text-[12px]">
          <span className="text-[color:var(--text-secondary)]">{r.agent_name || 'unknown agent'}</span>
          <span className="text-[color:var(--text-muted)]">{ago(r.created_at)}</span>
          <span className="ml-auto inline-flex items-center gap-1 font-medium">
            <Verdict passed={r.before} /> <span className="text-[color:var(--text-muted)]">→</span> <Verdict passed={r.after} />
          </span>
          <button type="button" onClick={() => onOpen(unitOf(r))} aria-label={`Open ${r.target_kind} ${r.eval_run_id}`} className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] text-[color:var(--text-secondary)] hover:bg-surface hover:text-[color:var(--text-primary)]">
            <ExternalLink className="h-3 w-3" /> Open
          </button>
        </li>
      ))}
    </ul>
  );
}

export default function Eval3ProposalsPage({ teamId, lineageId = null, onBack, onAll, onEdit }) {
  useBreadcrumb([]);
  useHideGlobalTopBar(true);
  const { confirm: confirmDialog, prompt: promptDialog, alert: alertDialog } = useAppDialog();

  // One feed, newest first, like notifications: no categories. What each
  // change proposes, and what is left to do about it, is on the item itself.
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(null);
  const [overlay, setOverlay] = useState(null);
  const [reload, setReload] = useState(0);

  const refresh = useCallback(() => setReload((n) => n + 1), []);

  useEffect(() => {
    if (!teamId) return undefined;
    let cancelled = false;
    setLoading(true);
    listProposals(teamId, { lineageId })
      .then((r) => {
        if (cancelled) return;
        setItems(r.items);
        setError(null);
        setSelectedId((cur) => (cur && r.items.some((i) => i.id === cur) ? cur : r.items[0]?.id || null));
      })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.error || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [teamId, lineageId, reload]);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return undefined; }
    let cancelled = false;
    setDetail(null);
    getProposal(teamId, selectedId)
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch((e) => { if (!cancelled) setDetail({ error: e?.response?.data?.error || e.message }); });
    return () => { cancelled = true; };
  }, [teamId, selectedId, reload]);

  const p = detail?.proposal;
  // A proposal with no change to the eval: the eval was right, the AGENT is what needs fixing.
  const agentOnly = Boolean(p) && Object.keys((p.diff && p.diff.nodes) || {}).length === 0 && !p.diff?.entry;
  const stepName = p ? (detail.base?.graph?.nodes?.[p.target_node_id]?.name || p.target_node_id) : '';
  const impact = p ? blastRadiusSentence(p.preview) : '';
  const regression = p ? regressionSentence(p.regression) : null;

  const failWith = (e, title) => alertDialog(e?.response?.data?.message || e?.response?.data?.error || e.message, { title });

  const approve = async () => {
    const which = detail.candidate_version ? `v${detail.candidate_version}` : 'a new version';
    const ok = await confirmDialog(
      [impact, regression?.text].filter(Boolean).join(' ') || 'This changes the eval.',
      { title: `Set ${which} of ${p.lineage_name} as production?`, confirmLabel: 'Approve' },
    );
    if (!ok) return;
    setBusy('approve');
    try {
      const out = await approveProposal(teamId, p.id);
      const bf = out?.backfill;
      if (bf && bf.status === 'awaiting_confirmation') {
        const e = bf.estimate || {};
        const go = await confirmDialog(
          `${bf.confirm_reason || ''} Estimated ${e.projected_units?.min ?? '?'}–${e.projected_units?.max ?? '?'} units. You can also start it later from the eval's versions.`,
          { title: `Re-score history under v${out.version}?`, confirmLabel: 'Re-score history', cancelLabel: 'Not now' },
        );
        if (go) await confirmBackfill(teamId, bf.id);
      }
      refresh();
    } catch (e) {
      failWith(e, 'Could not approve');
      refresh();
    } finally { setBusy(null); }
  };

  const reject = async () => {
    const reason = agentOnly
      ? await promptDialog('What was done about it? A line is enough.', { title: 'Mark as done', placeholder: 'e.g. added a rule to the system prompt', confirmLabel: 'Done' })
      : await promptDialog('Why not? The next proposal learns from this.', { title: 'Reject this change', placeholder: 'e.g. too broad: it would let real failures through', confirmLabel: 'Reject' });
    if (reason == null) return;
    if (!reason.trim()) { alertDialog('Say why, in a few words.', { title: 'A reason is needed' }); return; }
    setBusy('reject');
    try { await rejectProposal(teamId, p.id, reason.trim()); refresh(); } catch (e) { failWith(e, 'Could not reject'); } finally { setBusy(null); }
  };

  const revert = async () => {
    const ok = await confirmDialog(
      `Production goes back to v${p.based_on_version}. v${p.resulting_version} stays in the history and can be set as production again later.`,
      { title: `Revert the change to "${stepName}"?`, confirmLabel: 'Revert', danger: true },
    );
    if (!ok) return;
    setBusy('revert');
    try { await revertProposal(teamId, p.id); refresh(); } catch (e) { failWith(e, 'Could not revert'); } finally { setBusy(null); }
  };

  const filterName = lineageId ? (items[0]?.lineage_name || 'this eval') : null;
  const btn = 'inline-flex h-8 items-center gap-1.5 rounded-lg px-3 text-[12.5px] font-medium transition-colors disabled:opacity-50';
  const waiting = items.filter((i) => i.status === 'open').length;
  const groups = [];
  for (const it of items) {
    const g = dayGroup(it.created_at);
    if (!groups.length || groups[groups.length - 1].label !== g) groups.push({ label: g, items: [] });
    groups[groups.length - 1].items.push(it);
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-[color:var(--surface-base)]" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div className="flex h-10 shrink-0 items-center gap-1.5 border-b border-[color:var(--border-subtle)] px-4">
        <button type="button" onClick={onBack} className="text-[13px] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]">Evals</button>
        <ChevronRight className="h-3 w-3 text-[color:var(--text-muted)]" />
        <span className="text-[13px] font-medium text-[color:var(--text-primary)]">Proposed changes</span>
        {waiting > 0 && <span className="rounded-full bg-brand-soft px-2 py-px text-[10.5px] font-semibold text-brand">{waiting} waiting</span>}
        {filterName && (
          <span className="text-[12px] text-[color:var(--text-secondary)]">
            · {filterName} <button type="button" onClick={onAll} className="ml-1 underline decoration-dotted">all evals</button>
          </span>
        )}
      </div>

      {/* Nothing to read means nothing to read beside either: an empty feed in
          a 360px column with a blank page next to it looks like a page that
          failed to load. */}
      {!loading && !items.length && !error ? (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 px-8 text-center">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-soft text-brand"><Inbox className="h-5 w-5" aria-hidden /></span>
          <div className="text-[13px] font-medium text-[color:var(--text-primary)]">Nothing proposed yet</div>
          <div className="max-w-sm text-[12px] leading-relaxed text-[color:var(--text-muted)]">Lucid proposes a change when you ask it to improve an eval, or when a self-improving eval&apos;s alert fires.</div>
        </div>
      ) : (
      <div className="flex min-h-0 flex-1">
        {/* The feed: like notifications, newest first, grouped by day. */}
        <aside className="flex w-[360px] shrink-0 flex-col border-r border-[color:var(--border-subtle)] bg-[color:var(--surface-sunken)]/40">
          {error && <div className="m-3 rounded-lg bg-danger-soft p-2.5 text-[12px] text-danger">{error}</div>}
          {loading && !items.length && <div className="flex justify-center py-12"><BrandLoader label="Loading proposed changes" /></div>}
          <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
            {groups.map((g) => (
              <div key={g.label}>
                <div className="px-2 pb-1 pt-3 text-[10.5px] font-semibold uppercase tracking-wider text-[color:var(--text-muted)]">{g.label}</div>
                <div className="flex flex-col gap-0.5">
                  {g.items.map((it) => {
                    const on = it.id === selectedId;
                    const unread = it.status === 'open';
                    const flips = it.regression && it.regression.flipped > 0;
                    return (
                      <button key={it.id} type="button" onClick={() => setSelectedId(it.id)} aria-pressed={on}
                        aria-label={`${it.lineage_name}${unread ? ', waiting for you' : ''}: ${it.rationale || KIND_TITLE[it.proposal_kind]}`}
                        className={`relative flex w-full gap-2 rounded-xl px-2.5 py-2.5 text-left transition-colors ${on ? 'bg-surface shadow-token-sm ring-1 ring-[color:var(--border-subtle)]' : 'hover:bg-surface/70'}`}>
                        {/* Unread: a dot in the gutter, nothing more. */}
                        <span className={`mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full ${unread ? 'bg-brand' : ''}`} aria-hidden />
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-2">
                            <span className={`truncate text-[12.5px] ${unread ? 'font-semibold' : 'font-medium'} text-[color:var(--text-primary)]`}>{it.lineage_name}</span>
                            <span className="ml-auto shrink-0 text-[10.5px] text-[color:var(--text-muted)]">{ago(it.created_at)}</span>
                          </span>
                          <span className={`mt-0.5 line-clamp-2 text-[11.5px] leading-snug ${unread ? 'text-[color:var(--text-secondary)]' : 'text-[color:var(--text-muted)]'}`}>
                            {it.rationale || `${KIND_TITLE[it.proposal_kind]} · ${it.target_node_id}`}
                          </span>
                          <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
                            <StatusPill p={it} />
                            {it.resulting_version && <span className="text-[10.5px] text-[color:var(--text-muted)]">v{it.based_on_version} → v{it.resulting_version}</span>}
                            {!it.resulting_version && <span className="inline-flex items-center gap-1 text-[10.5px] text-[color:var(--text-muted)]"><Wrench className="h-3 w-3" aria-hidden />Fix in the agent</span>}
                            {flips && <span className="text-[10.5px] text-danger">breaks {it.regression.flipped} labelled</span>}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </aside>

        <div className="min-w-0 flex-1 overflow-y-auto">
          {selectedId && !detail && <PageLoader label="Loading this change" />}
          {detail?.error && <div className="m-4 rounded-lg bg-danger-soft p-3 text-[12px] text-danger">{detail.error}</div>}
          {!selectedId && !loading && items.length > 0 && (
            <div className="flex h-full items-center justify-center text-[12px] text-[color:var(--text-muted)]">Pick a change to see it.</div>
          )}
          {p && (
            <div className="mx-auto flex max-w-3xl flex-col gap-3 px-6 py-6" aria-label="Proposal">
              {/* Header: where it stands, what it is, and what to do: the decision sits with the title. */}
              <header className="rounded-2xl border border-[color:var(--border-subtle)] bg-surface p-5 shadow-token-sm">
                <div className="flex items-center gap-2">
                  <StatusPill p={p} />
                  <span className="min-w-0 truncate text-[12px] font-medium text-[color:var(--text-secondary)]">{p.lineage_name}</span>
                  <span className="flex-1" />
                  <button type="button" onClick={() => onEdit && onEdit(p.lineage_id)} className="inline-flex h-7 shrink-0 items-center gap-1 rounded-lg px-2 text-[11.5px] text-[color:var(--text-secondary)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]">
                    <ExternalLink className="h-3 w-3" /> Open the eval
                  </button>
                </div>
                <div className="mt-1 text-[11.5px] text-[color:var(--text-muted)]">
                  Proposed against v{p.based_on_version}{detail.current ? ` · production is v${detail.current.version}` : ''}{p.status === 'open' && detail.candidate_version ? ` · written as v${detail.candidate_version}, not running` : ''}
                </div>
                <h2 className="mt-3 text-[19px] font-semibold tracking-tight text-[color:var(--text-primary)]">{agentOnly ? 'Fix the agent' : KIND_TITLE[p.proposal_kind]}</h2>
                <div className="mt-1 text-[12.5px] text-[color:var(--text-secondary)]" data-testid="proposal-what">
                  {agentOnly
                    ? 'The eval judged these correctly. What needs to change is in the agent.'
                    : <>By {ARTIFACT[p.artifact_kind]} on the step “{stepName}”{p.diff?.diagnosis?.failure_mode ? `, which ${MODE_WORD[p.diff.diagnosis.failure_mode]}` : ''}.</>}
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-2" role="group" aria-label="Decision">
                  {p.status === 'open' && agentOnly && (
                    <button type="button" onClick={reject} disabled={Boolean(busy)} className={`${btn} bg-[color:var(--surface-sunken)] text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)]`}>
                      {busy === 'reject' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Done
                    </button>
                  )}
                  {p.status === 'open' && !agentOnly && (
                    <>
                      <button type="button" onClick={approve} disabled={Boolean(busy)} className={`${btn} bg-brand text-ink-onBrand shadow-token-sm hover:bg-brand-hover`}>
                        {busy === 'approve' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Put in production
                      </button>
                      <button type="button" onClick={reject} disabled={Boolean(busy)} className={`${btn} bg-[color:var(--surface-sunken)] text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)]`}>
                        {busy === 'reject' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />} Reject
                      </button>
                    </>
                  )}
                  {p.status === 'needs_human' && (
                    <>
                      <span className="text-[12px] text-[color:var(--text-secondary)]">No usable change could be made. Look at the evidence and edit the eval yourself, or dismiss this.</span>
                      <button type="button" onClick={reject} disabled={Boolean(busy)} className={`${btn} bg-[color:var(--surface-sunken)] text-[color:var(--text-secondary)]`}>Dismiss</button>
                    </>
                  )}
                  {['auto_promoted', 'approved'].includes(p.status) && (() => {
                    const word = p.status === 'auto_promoted' ? 'Applied automatically' : 'Put in production';
                    if (p.reverted_at) return <span className="text-[12px] text-[color:var(--text-muted)]">{word} as v{p.resulting_version}; reverted, production went back to v{p.based_on_version}.</span>;
                    if (p.production_version != null && p.production_version !== p.resulting_version) {
                      return <span className="text-[12px] text-[color:var(--text-muted)]">{word} as v{p.resulting_version}; production has since moved to v{p.production_version}.</span>;
                    }
                    return (
                      <>
                        <span className="inline-flex items-center gap-1.5 text-[12px] text-success"><Check className="h-3.5 w-3.5" aria-hidden />{word} as v{p.resulting_version}, in production{p.decision_reason ? ` · ${p.decision_reason}` : ''}.</span>
                        <button type="button" onClick={revert} disabled={Boolean(busy)} className={`${btn} bg-[color:var(--surface-sunken)] text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)]`}>
                          {busy === 'revert' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />} Revert
                        </button>
                      </>
                    );
                  })()}
                  {p.status === 'rejected' && <span className="text-[12px] text-[color:var(--text-muted)]">Rejected: {p.decision_reason}</span>}
                </div>
              </header>

              {regression && regression.tone === 'danger' && (
                <div role="alert" className="rounded-xl bg-danger-soft px-4 py-3 text-[13px] font-medium text-danger" data-testid="proposal-regression">{regression.text}</div>
              )}

              {p.rationale && (
                <Block label="Why" icon={Lightbulb}>
                  <div className="text-[13.5px] leading-relaxed text-[color:var(--text-primary)]" data-testid="proposal-why">{p.rationale}</div>
                </Block>
              )}

              {detail.agent_suggestion && (
                <Block label={`What to fix in the agent${detail.agent_suggestion.agent_part ? ` · ${detail.agent_suggestion.agent_part}` : ''}`} icon={Wrench} testId="proposal-agent" tone="brand">
                  <div className="text-[13.5px] font-medium text-[color:var(--text-primary)]">{detail.agent_suggestion.title}</div>
                  <div className="mt-1 text-[12.5px] leading-relaxed text-[color:var(--text-secondary)]">{detail.agent_suggestion.body}</div>
                  {detail.agent_suggestion.change && (
                    <div className="mt-2 rounded-lg bg-surface px-3 py-2 text-[12.5px] leading-relaxed text-[color:var(--text-primary)] shadow-token-sm">
                      <span className="font-medium">Change: </span>{detail.agent_suggestion.change}
                    </div>
                  )}
                </Block>
              )}

              {!agentOnly && <Block label="What it would change to the eval" icon={Activity} testId="proposal-impact">
                <div className="text-[13.5px] leading-relaxed text-[color:var(--text-primary)]">{impact || 'No preview is available for this change.'}</div>
                {regression && regression.tone !== 'danger' && (
                  <div className={`mt-2 rounded-lg px-3 py-2 text-[12px] ${regression.tone === 'ok' ? 'bg-success-soft text-success' : 'bg-[color:var(--surface-sunken)] text-[color:var(--text-secondary)]'}`}>{regression.text}</div>
                )}
                {detail.examples?.regressions?.length > 0 && (
                  <div className="mt-3"><div className="mb-1.5 text-[11px] font-medium text-danger">Labelled verdicts it would get wrong</div><FlipList rows={detail.examples.regressions} onOpen={setOverlay} /></div>
                )}
                {detail.examples?.pass_to_fail?.length > 0 && (
                  <div className="mt-3"><div className="mb-1.5 text-[11px] font-medium text-[color:var(--text-muted)]">Would now fail</div><FlipList rows={detail.examples.pass_to_fail} onOpen={setOverlay} /></div>
                )}
                {detail.examples?.fail_to_pass?.length > 0 && (
                  <div className="mt-3"><div className="mb-1.5 text-[11px] font-medium text-[color:var(--text-muted)]">Would now pass</div><FlipList rows={detail.examples.fail_to_pass} onOpen={setOverlay} /></div>
                )}
                {p.diff?.stratum && (
                  <div className="mt-3 text-[12px] leading-relaxed text-[color:var(--text-secondary)]">
                    The judge passed {p.diff.stratum.passed} of {p.diff.stratum.runs} runs over {p.diff.stratum.window_days} days{p.diff.stratum.route && p.diff.stratum.route !== '*' ? ` on the “${p.diff.stratum.route}” route` : ''}.
                    The screen recognised {p.diff.stratum.screened_in_sample} of {p.diff.stratum.sample} sampled runs, and passed none the judge failed.
                  </div>
                )}
              </Block>}

              {detail.proposed_graph && !agentOnly && (
                <Block label="Where it changes the eval" icon={GitBranch}>
                  <ProposalGraph base={detail.base?.graph} proposed={detail.proposed_graph} target={p.target_node_id} />
                </Block>
              )}

              {detail.evidence?.length > 0 && (
                <Block label={`Why this came up · ${detail.evidence_total} ${detail.evidence_total === 1 ? 'trace' : 'traces'}`} icon={History} testId="proposal-evidence">
                  <ul className="flex flex-col gap-1">
                    {detail.evidence.map((e) => (
                      <li key={e.eval_run_id} className="flex items-start gap-2.5 rounded-lg bg-[color:var(--surface-sunken)] px-3 py-2 text-[12px]">
                        <Verdict passed={e.passed} />
                        <div className="min-w-0 flex-1">
                          <span className="text-[color:var(--text-primary)]">
                            {/* A run that never reached a verdict did not say
                                anything, least of all FAIL. */}
                            {e.state && e.state !== 'resolved' ? `The eval ${e.state === 'errored' ? 'errored' : e.state}` : `The eval said ${verdictWord(e.passed)}`}
                          </span>
                          {e.correction && <span className="text-[color:var(--text-secondary)]">; a person said it should {e.correction.should_pass ? 'pass' : 'fail'}</span>}
                          {e.correction?.reason && <div className="mt-0.5 italic text-[color:var(--text-muted)]">“{e.correction.reason}”</div>}
                        </div>
                        <button type="button" onClick={() => setOverlay(unitOf(e))} className="inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] text-[color:var(--text-secondary)] hover:bg-surface hover:text-[color:var(--text-primary)]">
                          <ExternalLink className="h-3 w-3" /> Open
                        </button>
                      </li>
                    ))}
                  </ul>
                </Block>
              )}

            </div>
          )}
        </div>
      </div>
      )}

      {overlay?.kind === 'run' && <div className="fixed inset-0 z-[220]"><AgentRunDetailPage teamId={teamId} runId={overlay.id} onClose={() => setOverlay(null)} /></div>}
      {overlay?.kind === 'span' && <div className="fixed inset-0 z-[220]"><TraceDetailPage teamId={teamId} spanId={overlay.id} onClose={() => setOverlay(null)} onRunClick={(id) => setOverlay({ kind: 'run', id })} onSpanClick={(id) => setOverlay({ kind: 'span', id })} /></div>}
      {overlay?.kind === 'conversation' && <div className="fixed inset-0 z-[220]"><ConversationDetailDrawer teamId={teamId} conversationId={overlay.id} onClose={() => setOverlay(null)} /></div>}
    </div>
  );
}
